import styled from "@emotion/styled";
import { Add, Remove } from "@mui/icons-material";
import { Box, CircularProgress, Table } from "@mui/joy";
import { Link, useParams } from "@tanstack/react-router";
import {
  defaultRangeExtractor,
  useVirtualizer,
  VirtualItem,
  Virtualizer,
} from "@tanstack/react-virtual";
import {
  ApiCompetition,
  ApiCompetitionBulkAttemptData,
  ApiCompetitionEntryInline,
} from "griptonite-api-client";
import { orderBy } from "lodash";
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";

import api from "@/core/api";
import queryClient from "@/core/queryClient";
import { Route } from "@/core/types";
import {
  useCompetitionCategoriesByUuid,
  useCompetitionQuery,
  useCompetitionUserAttemptsByRoute,
} from "@/hooks/competitions";
import { useCompetitionRoutesByUuid, useCompetitionRoutesQuery } from "@/hooks/routes";
import { useCurrentVenueUuid } from "@/hooks/venues";
import formatIFSCScoreTuple from "@/utils/formatIFSCScoreTuple";
import formatNumber, { getNumberWithOrdinal } from "@/utils/formatNumber";
import getCompetitionRouteDisplay from "@/utils/getCompetitionRouteDisplay";

import Avatar from "../Avatar";
import { RouteCircle } from "../RouteCircle";
import {
  calculateScoreChange,
  useEntriesEditContext,
  useEntryAttemptEditState,
} from "./CompetitionEntriesEditContext";

type Props = { competitionUuid: string; entries: ApiCompetitionEntryInline[] };

const CompetititorCellStyled = styled.div`
  div.user {
    display: flex;
    align-items: center;
    gap: 4px;
    p {
      font-size: 1rem;
      margin: 0;
    }
  }
  div.position {
    font-weight: normal;
  }
  div.score {
    font-weight: bold;
    .delta {
      display: inline-block;
      &.positive {
        background-color: #45d27e;
      }
      &.negative {
        background-color: #fd5348;
      }
      color: white;
      border-radius: 8px;
      padding: 0 4px;
    }
  }
`;

const RowLoadingContainer = styled.div`
  position: sticky;
  top: 0;
  left: 200px;
  bottom: 0;
  width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const useCalculateScoreChange = ({
  entry,
  competition,
}: {
  entry: ApiCompetitionEntryInline;
  competition?: ApiCompetition;
}) => {
  const venueUuid = useCurrentVenueUuid();
  const { data: routesByUuid } = useCompetitionRoutesByUuid(competition?.uuid, venueUuid);
  const { data: attemptsByRoute } = useCompetitionUserAttemptsByRoute(
    competition?.uuid,
    entry.user.uuid,
  );
  const attemptStatesMap = useEntriesEditContext((state) => state.attemptState);
  const score = useMemo(() => {
    if (!competition) {
      return undefined;
    }
    const mapEntries = Object.entries(attemptStatesMap);
    if (!mapEntries.length) {
      return undefined;
    }
    const matchingStates = mapEntries.filter(([key]) => key.startsWith(entry.user.uuid));
    if (matchingStates.length === 0) {
      return undefined;
    }
    if (matchingStates[0][1] === undefined) {
      return undefined;
    }
    return matchingStates.reduce((acc, [key, state]) => {
      const [, routeUuid] = key.split(":");
      const route = routesByUuid?.[routeUuid];
      if (!route) {
        return acc;
      }
      const attemptInfo = attemptsByRoute?.[routeUuid];
      const scoreChange = calculateScoreChange({
        competition,
        entry,
        route,
        attemptInfo,
        state,
      });
      if (scoreChange === undefined) {
        return acc;
      }
      return acc + scoreChange;
    }, 0);
  }, [entry, competition, routesByUuid, attemptsByRoute, attemptStatesMap]);

  return score;
};

const CompetitorCell = React.memo(
  ({ competition, entry }: { competition?: ApiCompetition; entry: ApiCompetitionEntryInline }) => {
    const categoriesByUuid = useCompetitionCategoriesByUuid(competition?.uuid);
    const scoreChange = useCalculateScoreChange({ entry, competition });

    const category = categoriesByUuid?.[entry.category];

    return (
      <Link
        to="./$userUuid"
        from="/gym/$venueUuid/events/$competitionUuid/competitors"
        params={{ userUuid: entry.user.uuid }}
        style={{ textDecoration: "none", color: "black", width: "100%" }}
      >
        <CompetititorCellStyled>
          <div className="user">
            <Avatar user={entry.user} size={24} />
            <p>
              {entry.user.firstName} {entry.user.lastName}
            </p>
          </div>
          <div className="position">
            {entry.categoryPosition ? (
              <>
                <strong>{getNumberWithOrdinal(entry.categoryPosition)}</strong>, {category?.name}
              </>
            ) : (
              category?.name
            )}
          </div>
          <div className="score">
            {competition?.scoringSystem === "IFSC_RANKING"
              ? formatIFSCScoreTuple(entry.scoreTuple)
              : `${formatNumber(entry.score, {
                  maxDecimals: competition?.scoringSystem === "IFSC_SPORT" ? 2 : 1,
                })}pts`}
            {scoreChange ? (
              <>
                {" "}
                <div className={`delta ${scoreChange > 0 ? "positive" : "negative"}`}>
                  {scoreChange > 0 ? "+" : ""}
                  {formatNumber(scoreChange, { maxDecimals: 1 })}pts
                </div>
              </>
            ) : null}
          </div>
        </CompetititorCellStyled>
      </Link>
    );
  },
);
CompetitorCell.displayName = "CompetitorCell";

const RouteHeader = React.memo(({ route }: { route: Route }) => {
  const selectedCategoryUuid = useEntriesEditContext((state) => state.selectedCategoryUuid);
  const routeGroup = useMemo(() => {
    return selectedCategoryUuid
      ? route.competitionRouteGroups.find((group) =>
          group.categories.includes(selectedCategoryUuid),
        )
      : undefined;
  }, [route, selectedCategoryUuid]);

  // let number = route.circuitNumber;
  // if (route.displayRouteGroupMapping[routeGroup?.uuid || ""]) {
  //   number = route.displayRouteGroupMapping[routeGroup?.uuid || ""].number;
  // }
  const name = getCompetitionRouteDisplay(route, {
    routeGroups: route.competitionRouteGroups,
    routeGroupUuid: routeGroup?.uuid,
  }).name;

  return (
    <Link
      to="../routes/$routeUuid"
      from="/gym/$venueUuid/events/$competitionUuid/competitors"
      params={{
        routeUuid: route.uuid,
      }}
      style={{ display: "flex", textDecoration: "none", color: "black" }}
    >
      <div style={{ marginRight: 4 }}>
        <RouteCircle colors={(route.colors || "#FFFFFF").split(":")} />
      </div>
      {name}
    </Link>
  );
});
RouteHeader.displayName = "RouteHeader";

export default function CompetitionEntriesTable({ competitionUuid, entries }: Props) {
  const venueUuid = useCurrentVenueUuid();

  const { selectedCategoryUuid } = useEntriesEditContext();

  const routesQuery = useCompetitionRoutesQuery(competitionUuid, venueUuid);

  const routes = useMemo(() => {
    if (!selectedCategoryUuid) {
      return orderBy(routesQuery.data || [], "circuitNumber");
    }
    return orderBy(
      (routesQuery.data || []).filter((route) =>
        route.competitionRouteGroups.some((crg) => crg.categories.includes(selectedCategoryUuid)),
      ),
      (route) => {
        const number =
          route.displayRouteGroupMapping[
            route.competitionRouteGroups.find((crg) =>
              crg.categories.includes(selectedCategoryUuid),
            )?.uuid || ""
          ]?.number || route.circuitNumber;
        return number;
      },
    );
  }, [routesQuery.data, selectedCategoryUuid]);

  const { data: competition } = useCompetitionQuery(competitionUuid);

  const entriesLoaded = entries.length > 0;

  useEffect(() => {
    if (!competitionUuid || entriesLoaded) return;
    let active = true;

    (async () => {
      let page = 1;
      while (active) {
        const data = await api.competitions.getCompetitionAttempts(competitionUuid, { page });
        if (!active || data.attempts.length === 0) break;

        const byUser: Record<string, ApiCompetitionBulkAttemptData[]> = {};
        data.attempts.forEach((a) => (byUser[a.user] ??= []).push(a));
        Object.entries(byUser).forEach(([uuid, attempts]) => {
          queryClient.setQueryData(
            ["competitions", competitionUuid, "attempts", "user", uuid],
            attempts,
          );
        });
        page++;
      }
    })();

    return () => {
      active = false;
    };
  }, [competitionUuid, entriesLoaded]);

  const rowHeight = competition?.scoringSystem == "IFSC_RANKING" ? 120 : 80;

  const rowVirtualizer = useVirtualizer({
    count: entries.length,
    estimateSize: () => rowHeight,
    getItemKey: useCallback((index: number) => entries[index].user.uuid, [entries]),
    overscan: 8,
    getScrollElement: () => wrapperRef.current,
  });

  const wideInputs = useMemo(() => {
    const hasMultipleZones = routes.some((route) => (route.segmentsCount || 0) > 1);
    const isBoulder = competition?.climbType === "BOULDER";
    return hasMultipleZones && isBoulder;
  }, [routes, competition?.climbType]);

  const columnVirtualizer = useVirtualizer({
    count: routes.length + 1,
    estimateSize: (index: number) => (index === 0 ? 200 : wideInputs ? 150 : 120),
    getItemKey: useCallback(
      (index: number) => (index === 0 ? "competitor" : routes[index - 1].uuid),
      [routes],
    ),
    overscan: 5,
    horizontal: true,
    getScrollElement: () => wrapperRef.current,
    rangeExtractor: (range) => {
      return [...new Set([0, ...defaultRangeExtractor(range)])];
    },
  });

  const { userUuid: selectedUserUuid } = useParams({ strict: false });

  const entriesRef = useRef(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  useEffect(() => {
    if (selectedUserUuid) {
      const index = entriesRef.current.findIndex((entry) => entry.user.uuid === selectedUserUuid);
      if (index !== -1) {
        rowVirtualizer.scrollToIndex(index);
      }
    }
  }, [selectedUserUuid, rowVirtualizer]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  if (!competition) {
    return null;
  }

  return (
    <Box ref={wrapperRef} sx={{ position: "relative", overflow: "auto", flex: 1, width: "100%" }}>
      <Table
        sx={{
          display: "grid",
          touchAction: "none",
          "tbody tr th": {
            borderBottom: "1px solid",
            borderBottomColor: "var(--TableCell-borderColor)",
          },
          "th, td": {
            borderRight: "1px solid",
            borderRightColor: "var(--TableCell-borderColor)",
          },
          "tbody tr td": {
            padding: 0,
          },
        }}
      >
        <thead
          style={{
            display: "grid",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <tr style={{ width: "100%", display: "flex" }}>
            <TableHeader items={columnVirtualizer.getVirtualItems()} routes={routes} />
          </tr>
        </thead>
        <tbody
          style={{
            position: "relative",
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: `${columnVirtualizer.getTotalSize()}px`,
            display: "grid",
          }}
        >
          <TableRows
            columnVirtualizer={columnVirtualizer}
            rowVirtualizer={rowVirtualizer}
            entries={entries}
            routes={routes}
            competition={competition}
          />
        </tbody>
      </Table>
    </Box>
  );
}

const Style = {
  CounterWrapper: styled.div`
    display: flex;
    align-items: center;
    gap: 2px;

    input {
      flex: 1;
      min-width: 0;
      width: auto;
      height: 24px;
      border: 1px solid var(--joy-palette-neutral-400);
      border-radius: 4px;
      font-family: var(--joy-fontFamily-body);
      padding: 2px 4px;
      -moz-appearance: textfield;
      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
      }
    }
    button {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      outline: none;
      border: none;
      border-radius: 4px;
      color: white;
      cursor: pointer;
      background-color: var(--joy-palette-neutral-100);

      .changed & {
        background-color: white;
      }
    }
    button:hover {
      background-color: var(--joy-palette-neutral-200);
    }
    svg {
      color: var(--joy-palette-neutral-700);
    }
  `,

  CheckboxesWrapper: styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-start;
    width: 100%;
    flex: 1;
  `,

  AttemptsCellWrapper: styled.div`
    padding: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    justify-content: center;
    flex-direction: column;
    flex: 1;

    &.changed {
      background-color: var(--joy-palette-primary-100);
    }

    &.error {
      background-color: var(--joy-palette-danger-100);
    }
  `,

  TriesInputWrapper: styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    label {
      font-size: 0.75rem;
    }
  `,

  CheckboxWrapper: styled.div`
    label {
      display: flex;
      align-items: center;
      font-size: 0.75rem;
      gap: 4px;
    }
    input {
      appearance: none;
      background-color: white;
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border: 1px solid var(--joy-palette-neutral-400);
      border-radius: 4px;
      box-shadow: none;
    }
    input:checked {
      background-image: var(--joy-palette-primary-solidGradientBg);
      border: none;
    }
    input:checked::before {
      content: "";
      display: block;
      width: 100%;
      height: 100%;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M9 16.17 5.53 12.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l4.18 4.18c.39.39 1.02.39 1.41 0L20.29 7.71c.39-.39.39-1.02 0-1.41a.9959.9959 0 0 0-1.41 0L9 16.17z'%3E%3C/path%3E%3C/svg%3E");
    }
  `,

  QDSelect: styled.select`
    font-size: 0.75rem;
    padding: 4px;
    padding-right: 18px;
    border: 1px solid var(--joy-palette-neutral-400);
    border-radius: 4px;
    background-color: white;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='var(--joy-palette-neutral-700)' d='M7 10l5 5 5-5z'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right center;
    flex: 1;
    margin-left: 15px;
  `,
};

const AttemptsCell = memo(
  ({
    attemptsInfo,
    route,
    entry,
    competition,
  }: {
    attemptsInfo?: ApiCompetitionBulkAttemptData;
    route: Route;
    entry: ApiCompetitionEntryInline;
    competition?: ApiCompetition;
  }) => {
    const wrongCategory = !route.competitionRouteGroups.some((x) =>
      x.categories.includes(entry.category),
    );
    const [state, setState] = useEntryAttemptEditState(route.uuid, entry.user.uuid);
    if (wrongCategory && !attemptsInfo) {
      return <div style={{ backgroundColor: "var(--joy-palette-neutral-100)", flex: 1 }}></div>;
    }

    const attemptsToZone = attemptsInfo?.segmentTriesCount;

    const triesId = `tries-${entry.user.uuid}-${route.uuid}`;
    const qdsId = `qds-${entry.user.uuid}-${route.uuid}`;
    const triesToZoneId = `triesToZone-${entry.user.uuid}-${route.uuid}`;
    const tickedId = `ticked-${entry.user.uuid}-${route.uuid}`;
    const zonedId = `zoned-${entry.user.uuid}-${route.uuid}`;

    const ticked = state?.success ?? (attemptsInfo?.success || false);
    const attemptSegmentsCount = state?.segmentsCount ?? attemptsInfo?.segmentsCount;
    const triesCount = state?.tries ?? attemptsInfo?.count;
    const triesToZoneCount = state?.segmentTries ?? attemptsToZone;

    return (
      <Style.AttemptsCellWrapper className={state ? "changed" : wrongCategory ? "error" : ""}>
        <Style.CheckboxesWrapper>
          <Style.CheckboxWrapper>
            <label htmlFor={tickedId}>
              <input
                type="checkbox"
                checked={!!ticked}
                id={tickedId}
                onChange={(e) => {
                  setState({
                    ...state,
                    success: e.target.checked,
                    tries: state?.tries || 1,
                    segmentsCount: e.target.checked ? 0 : state?.segmentsCount,
                  });
                }}
              />
              Top
            </label>
          </Style.CheckboxWrapper>
          {route.climbType === "BOULDER" && route.segmentsCount
            ? [...Array(route.segmentsCount)].map((_, i) => (
                <Style.CheckboxWrapper key={i}>
                  <label htmlFor={`${zonedId}-i`}>
                    <input
                      type="checkbox"
                      checked={!ticked && (attemptSegmentsCount ? attemptSegmentsCount > i : false)}
                      id={`${zonedId}-i`}
                      onChange={(e) => {
                        setState({
                          ...state,
                          segmentsCount: e.target.checked ? i + 1 : 0,
                          success: e.target.checked ? false : !!state?.success,
                          tries: state?.tries || 1,
                        });
                      }}
                    />
                    Z{i + 1}
                  </label>
                </Style.CheckboxWrapper>
              ))
            : null}
        </Style.CheckboxesWrapper>
        <Style.TriesInputWrapper>
          {route.climbType === "BOULDER" ? (
            <>
              <label htmlFor={triesId}>
                {competition?.scoringSystem === "IFSC_RANKING" ? "Tries total" : "Tries"}
              </label>
              <Counter
                id={triesId}
                value={triesCount}
                onChange={(value) =>
                  value !== undefined
                    ? setState({
                        ...state,
                        tries: value,
                        success: ticked ?? false,
                        segmentsCount: attemptSegmentsCount,
                        segmentTries:
                          competition?.scoringSystem === "IFSC_RANKING"
                            ? Math.min(value, triesToZoneCount || 1)
                            : undefined,
                      })
                    : undefined
                }
                min={ticked ? 1 : 0}
              />
            </>
          ) : (
            <>
              <label htmlFor={qdsId}>QDs</label>
              <Counter
                id={qdsId}
                value={!ticked ? (attemptSegmentsCount ?? undefined) : undefined}
                onChange={(value) =>
                  value !== undefined
                    ? setState({
                        ...state,
                        segmentsCount: value,
                        success: false,
                        tries: 1,
                      })
                    : undefined
                }
                min={0}
                max={(route.segmentsCount ?? 1) - 1}
              />
            </>
          )}
        </Style.TriesInputWrapper>
        {competition?.scoringSystem === "IFSC_RANKING" ? (
          <Style.TriesInputWrapper>
            <label htmlFor={triesToZoneId}>Tries to zone</label>
            <Counter
              id={triesToZoneId}
              value={triesToZoneCount ?? undefined}
              onChange={(value) =>
                value !== undefined
                  ? setState({
                      ...state,
                      segmentsCount: attemptSegmentsCount,
                      success: ticked ?? false,
                      tries: Math.max(triesCount || (ticked ? 1 : 0), value),
                      segmentTries: Math.max(value, attemptSegmentsCount ? 1 : 0),
                    })
                  : undefined
              }
              min={0}
            />
          </Style.TriesInputWrapper>
        ) : null}
      </Style.AttemptsCellWrapper>
    );
  },
);
AttemptsCell.displayName = "AttemptsCell";

export const Counter = memo(
  ({
    value,
    min,
    max,
    onChange,
    id,
  }: {
    value: number | undefined;
    min?: number;
    max?: number;
    onChange: (value: number) => void;
    id?: string;
  }) => {
    return (
      <Style.CounterWrapper>
        <button
          onClick={(e) => {
            e.preventDefault();
            onChange(Math.max(min ?? -Infinity, (value || 0) - 1));
          }}
        >
          <Remove />
        </button>
        <input
          type="number"
          id={id}
          autoComplete="off"
          name="attempts"
          min={min}
          max={max}
          step={1}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.valueAsNumber)}
        />
        <button
          onClick={(e) => {
            e.preventDefault();
            onChange(Math.min(max ?? Infinity, (value || 0) + 1));
          }}
        >
          <Add />
        </button>
      </Style.CounterWrapper>
    );
  },
);

Counter.displayName = "Counter";

const TableHeader = React.memo(({ items, routes }: { items: VirtualItem[]; routes: Route[] }) => {
  return items.map((virtualCol) => {
    const routeIndex = virtualCol.index - 1;
    if (virtualCol.index === 0) {
      return (
        <th
          key="competitor"
          style={{
            display: "flex",
            width: virtualCol.size,
            position: "sticky",
            left: 0,
            backgroundColor: "white",
            zIndex: 1,
          }}
        >
          Competitor
        </th>
      );
    }
    const route = routes[routeIndex];
    return (
      <th
        key={route.uuid}
        style={{
          display: "flex",
          width: virtualCol.size,
          position: "absolute",
          left: 0,
          transform: `translateX(${virtualCol.start}px)`,
        }}
      >
        <RouteHeader route={route} />
      </th>
    );
  });
});
TableHeader.displayName = "TableHeader";

const TableRow = React.memo(
  ({
    items,
    entry,
    routes,
    competition,
  }: {
    items: VirtualItem[];
    entry: ApiCompetitionEntryInline;
    routes: Route[];
    competition?: ApiCompetition;
  }) => {
    const { data: attemptsByRoute, isLoading } = useCompetitionUserAttemptsByRoute(
      competition?.uuid,
      entry.user.uuid,
    );

    const rowHeight = competition?.scoringSystem === "IFSC_RANKING" ? 120 : 80;

    return (
      <>
        {items.map((virtualCol) => {
          const routeIndex = virtualCol.index - 1;
          if (virtualCol.index === 0) {
            return (
              <th
                key="competitor"
                style={{
                  width: virtualCol.size,
                  display: "flex",
                  position: "sticky",
                  left: 0,
                  height: rowHeight,
                  backgroundColor: "white",
                  zIndex: 1,
                }}
              >
                <CompetitorCell entry={entry} competition={competition} />
              </th>
            );
          }
          const route = routes[routeIndex];
          return (
            <td
              key={route.uuid}
              style={{
                width: virtualCol.size,
                height: rowHeight,
                display: "flex",
                position: "absolute",
                left: 0,
                transform: `translateX(${virtualCol.start}px)`,
              }}
            >
              {isLoading ? (
                <div
                  style={{
                    flex: 1,
                    backgroundColor: "var(--joy-palette-neutral-100)",
                  }}
                />
              ) : (
                <AttemptsCell
                  attemptsInfo={attemptsByRoute?.[route.uuid]}
                  route={route}
                  entry={entry}
                  competition={competition}
                />
              )}
            </td>
          );
        })}
        {isLoading ? (
          <RowLoadingContainer>
            <CircularProgress size="sm" color="secondary" />
          </RowLoadingContainer>
        ) : null}
      </>
    );
  },
);
TableRow.displayName = "TableRow";

const TableRows = ({
  rowVirtualizer,
  columnVirtualizer,
  entries,
  routes,
  competition,
}: {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  entries: ApiCompetitionEntryInline[];
  routes: Route[];
  competition?: ApiCompetition;
}) => {
  const columnItems = columnVirtualizer.getVirtualItems();

  return rowVirtualizer.getVirtualItems().map((virtualRow) => {
    const entry = entries[virtualRow.index];
    return (
      <tr
        key={entry.user.uuid}
        style={{
          transform: `translateY(${virtualRow.start}px)`,
          position: "absolute",
          display: "flex",
          width: "100%",
        }}
      >
        <TableRow items={columnItems} entry={entry} routes={routes} competition={competition} />
      </tr>
    );
  });
};
// TableRows.displayName = "TableRows";
